import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    HostListener,
    computed,
    inject,
    signal,
} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {takeUntilDestroyed, toSignal} from '@angular/core/rxjs-interop';
import {
    AbstractControl,
    AsyncValidatorFn,
    NonNullableFormBuilder,
    ReactiveFormsModule,
    ValidationErrors,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import {AppValidators, TRANSLATE, TranslatePipe} from '@brunata-angular/util';
import {
    InputSelectOptionGroupComponent,
    InputTextAreaGroupComponent,
    InputTextGroupComponent,
} from '@brunata-angular/ui';
import {ToastrService} from 'ngx-toastr';
import {catchError, map, Observable, of, switchMap, timer} from 'rxjs';
import {
    UserAdministrationService,
    CreateUserRequest,
} from '@shared/services/user-administration.service';
import {SystemAccessPickerComponent} from '../system-access-picker/system-access-picker.component';
import {HasUnsavedChanges} from '@brunata-angular/ui';

@Component({
    selector: 'app-create-user',
    standalone: true,
    imports: [
        TranslatePipe,
        ReactiveFormsModule,
        InputTextGroupComponent,
        InputTextAreaGroupComponent,
        InputSelectOptionGroupComponent,
        SystemAccessPickerComponent,
    ],
    templateUrl: './create-user.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateUserComponent implements HasUnsavedChanges {
    private readonly userService = inject(UserAdministrationService);
    private readonly router = inject(Router);
    private readonly route = inject(ActivatedRoute);
    private readonly fb = inject(NonNullableFormBuilder);
    private readonly toastr = inject(ToastrService);
    private readonly translate = inject(TRANSLATE);
    private readonly destroyRef = inject(DestroyRef);

    readonly roleOptions = new Map<string, string>([
        [
            'ADMINISTRATOR',
            this.translate('properties_roles.administrator', 'online', 'userAdministration'),
        ],
        ['USER', this.translate('properties_roles.user', 'online', 'userAdministration')],
    ]);

    readonly formGroup = this.fb.group({
        personName: ['', Validators.required],
        userName: [
            '',
            {
                validators: [
                    Validators.required,
                    Validators.minLength(6),
                    Validators.maxLength(50),
                    this.usernameCharactersValidator(),
                ],
                asyncValidators: [this.usernameAvailableValidator()],
            },
        ],
        email: ['', [Validators.required, AppValidators.email]],
        role: ['', Validators.required],
        note: ['', Validators.maxLength(100)],
    });

    readonly isSaving = signal(false);
    readonly assignedBuildingNos = signal<number[]>([]);

    // Once the user has been created we navigate away deliberately, so the unsaved-changes
    // guard must not challenge that programmatic navigation.
    private saved = false;

    private readonly roleValue = toSignal(this.formGroup.controls.role.valueChanges, {
        initialValue: this.formGroup.controls.role.value,
    });
    protected readonly showSystemAccess = computed(
        () => this.roleValue() !== 'ADMINISTRATOR' && this.roleValue() !== '',
    );

    onSubmit(): void {
        if (this.formGroup.invalid) {
            // Reveal the validation messages instead of silently doing nothing.
            this.formGroup.markAllAsTouched();
            return;
        }
        if (this.formGroup.pending || this.isSaving()) {
            return;
        }

        const formValue = this.formGroup.getRawValue();
        const isAdministrator = formValue.role.toUpperCase() === 'ADMINISTRATOR';
        // formGroup mirrors CreateUserRequest exactly, so getRawValue() is the request.
        const request: CreateUserRequest = formValue;

        const buildingNos = this.assignedBuildingNos().map(String);

        this.isSaving.set(true);
        this.formGroup.disable();

        this.userService
            .createUser(request)
            .pipe(
                switchMap(createdUser =>
                    isAdministrator
                        ? of(createdUser)
                        : this.userService
                              .updateUserBuildingAccess(createdUser.loginNameId, buildingNos)
                              .pipe(map(() => createdUser)),
                ),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: () => {
                    this.saved = true;
                    this.toastr.success(
                        this.translate('alerts_createUserSuccess', 'online', 'userAdministration'),
                    );
                    void this.router.navigate(['../'], {relativeTo: this.route});
                },
                // The success toast comes from here; the error toast is shown by the global
                // ErrorInterceptor, so only the form needs re-enabling on failure.
                error: () => {
                    this.isSaving.set(false);
                    this.formGroup.enable();
                },
            });
    }

    private usernameCharactersValidator(): ValidatorFn {
        const allowed = /^[a-zA-Z0-9_-]*$/;
        return (control: AbstractControl<string>): ValidationErrors | null =>
            allowed.test(control.value)
                ? null
                : {
                      customValidationMessage: this.translate(
                          'validation_usernameCharacters',
                          'online',
                          'userAdministration',
                      ),
                  };
    }

    private usernameAvailableValidator(): AsyncValidatorFn {
        return (control: AbstractControl<string>): Observable<ValidationErrors | null> => {
            const username = control.value;
            if (username.length < 6) {
                return of(null);
            }
            return timer(350).pipe(
                switchMap(() => this.userService.checkUsernameExists(username)),
                map(taken =>
                    taken
                        ? {
                              customValidationMessage: this.translate(
                                  'validation_usernameAlreadyExists',
                                  'online',
                                  'userAdministration',
                              ),
                          }
                        : null,
                ),
                // A failed availability check must not strand the control in PENDING;
                // treat it as available so the form can still be submitted.
                catchError(() => of(null)),
            );
        };
    }

    hasUnsavedChanges(): boolean {
        return this.formGroup.dirty && !this.saved;
    }

    @HostListener('window:beforeunload', ['$event'])
    onBeforeUnload(event: BeforeUnloadEvent): void {
        if (this.hasUnsavedChanges()) {
            event.preventDefault();
        }
    }
}
